/**
 * The `sync_push_outcome` event contract (OCT-14720).
 *
 * Published by the push pipeline (Apideck `executePushEntity` and the native
 * `runPush` runner) after every bill or bill payment push settles, and
 * forwarded to open browser tabs by the SSE broadcaster, which relays every
 * outbox event. The bill detail page listens for it to refresh its sync state
 * and, on a failure only, to show a toast with a Retry action.
 *
 * Shared by the server (producer) and the client (consumer) so the two can
 * never drift on field names.
 */

export const SYNC_PUSH_OUTCOME_EVENT = "sync_push_outcome" as const;

/** The record types this event covers. */
export type SyncPushOutcomeEntityType = "bill" | "bill_payment";

export interface SyncPushOutcomePayload {
  /** `failed` includes a payment held back because its bill is not synced. */
  outcome: "succeeded" | "failed";
  /** What was pushed: the bill itself, or one of its payments. */
  pushedEntityType: SyncPushOutcomeEntityType;
  /** The bill the page is showing - the bill itself, or the payment's bill. */
  billId: string;
  /** The payment id for a `bill_payment` push; `null` for a bill push. */
  paymentId: string | null;
  /**
   * The OCTA entity that owns the bill (D23). Named `octaEntityId`, not
   * `entityId`: on an outbox envelope `entityId` is the aggregate id.
   */
  octaEntityId: string;
  /**
   * The sync ledger `provider` the row was written under (`apideck`, `xero`,
   * `zoho-books`, ...). The Retry action passes it to the ledger retry
   * endpoint, which needs it to pick the row.
   */
  ledgerProvider: string;
  /** Display name for copy, e.g. "QuickBooks Online". */
  providerLabel: string;
  /** Short user-safe reason (`summarizePushFailureReason`); `null` on success. */
  reason: string | null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Read a `sync_push_outcome` payload off an SSE frame, or `null` when the
 * frame is not one. Accepts both the broadcast envelope (`{ payload }`) and a
 * bare payload, the same way the header nav reads SSE frames.
 */
export function readSyncPushOutcome(frame: unknown): SyncPushOutcomePayload | null {
  if (!frame || typeof frame !== "object") return null;
  const envelope = frame as { payload?: unknown; };
  const raw = (envelope.payload && typeof envelope.payload === "object" ? envelope.payload : frame) as Record<
    string,
    unknown
  >;
  const { outcome, pushedEntityType, billId, paymentId, octaEntityId, ledgerProvider, providerLabel, reason } = raw;
  if (outcome !== "succeeded" && outcome !== "failed") return null;
  if (pushedEntityType !== "bill" && pushedEntityType !== "bill_payment") return null;
  if (!isNonEmptyString(billId) || !isNonEmptyString(octaEntityId) || !isNonEmptyString(ledgerProvider)) {
    return null;
  }
  if (pushedEntityType === "bill_payment" && !isNonEmptyString(paymentId)) return null;
  return {
    outcome,
    pushedEntityType,
    billId,
    paymentId: pushedEntityType === "bill_payment" ? (paymentId as string) : null,
    octaEntityId,
    ledgerProvider,
    providerLabel: typeof providerLabel === "string" ? providerLabel : "",
    reason: typeof reason === "string" && reason.length > 0 ? reason : null,
  };
}
